import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@reflex/ui/components/ui/dropdown-menu';
import { ToolbarIconButton } from '@reflex/ui/components/shared/ToolbarIconButton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@reflex/ui/components/ui/tooltip';
import { cn } from '@reflex/ui/lib/utils';
import type { AgentAction } from './useAgentActions';

interface AgentActionIconControlProps {
  action: AgentAction;
  onOpenDialog: (key: string) => void;
  /** Tooltip / menu placement for dense vertical rails vs horizontal headers. */
  side?: 'top' | 'right' | 'bottom' | 'left';
  className?: string;
}

/**
 * One agent action as a compact icon control — shared by the header inline
 * chrome and the rail pin strip so Mark as / Share / Stop never drift.
 */
export function AgentActionIconControl({
  action,
  onOpenDialog,
  side = 'bottom',
  className,
}: AgentActionIconControlProps) {
  if (action.kind === 'submenu') {
    return (
      <DropdownMenu>
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="toolbar-icon"
                className={cn('text-muted-foreground', className)}
                disabled={action.pending}
                aria-label={action.label}
                data-tutorial-id={`agent-action-${action.key}`}
                data-testid={`agent-action-${action.key}`}
              >
                <action.icon className={cn('size-4', action.iconClassName)} />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side={side}>{action.label}</TooltipContent>
        </Tooltip>
        <DropdownMenuContent side={side} align="end">
          {action.items.map((item) => (
            <DropdownMenuItem key={item.key} onSelect={item.onSelect}>
              <item.icon className={cn('mr-2 h-4 w-4', item.iconClassName)} />
              {item.label}
              {item.checked && <Check className="ml-auto h-4 w-4" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  if (action.kind === 'run') {
    return (
      <ToolbarIconButton
        icon={action.icon}
        label={action.label}
        tooltipSide={side}
        disabled={action.pending}
        onClick={action.onSelect}
        data-replay-target={action.replayTarget}
        data-tutorial-id={`agent-action-${action.key}`}
        data-testid={`agent-action-${action.key}`}
        className={cn(action.destructive && 'text-destructive', className)}
      />
    );
  }

  if (action.kind === 'dialog') {
    return (
      <ToolbarIconButton
        icon={action.icon}
        label={action.label}
        tooltipSide={side}
        disabled={action.pending}
        onClick={() => onOpenDialog(action.key)}
        data-tutorial-id={`agent-action-${action.key}`}
        data-testid={`agent-action-${action.key}`}
        className={cn(action.destructive && 'text-destructive', className)}
      />
    );
  }

  if (action.kind === 'link') {
    return (
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="toolbar-icon"
            className={cn('text-muted-foreground', className)}
            asChild
            data-testid={`agent-action-${action.key}`}
          >
            <Link to={action.to} aria-label={action.label}>
              <action.icon className="size-4" />
            </Link>
          </Button>
        </TooltipTrigger>
        <TooltipContent side={side}>{action.label}</TooltipContent>
      </Tooltip>
    );
  }

  return null;
}
